import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Zap, 
  Heart,
  Globe,
  Award,
  Mail
} from 'lucide-react'

const CareersPage = () => {
  const openPositions = [
    {
      id: 'senior-security-engineer',
      title: 'Senior Security Engineer',
      department: 'Security',
      location: 'Indore M.P, India / Remote',
      type: 'Full-time',
      description: 'Lead security assessments, penetration testing, and security architecture design for enterprise clients.'
    },
    {
      id: 'full-stack-developer',
      title: 'Full-Stack Developer',
      department: 'Development',
      location: 'Indore M.P, India / Remote',
      type: 'Full-time',
      description: 'Build secure, scalable applications using modern frameworks and cloud technologies.'
    },
    {
      id: 'security-consultant',
      title: 'Security Consultant',
      department: 'Consulting',
      location: 'Indore M.P, India / Remote',
      type: 'Full-time',
      description: 'Provide strategic security guidance and compliance consulting to enterprise clients.'
    }
  ]

  const benefits = [
    {
      title: 'Competitive Salary',
      description: 'Above-market compensation with equity options',
      icon: Award
    },
    {
      title: 'Remote First',
      description: 'Work from anywhere with flexible hours',
      icon: Globe
    },
    {
      title: 'Health & Wellness',
      description: 'Comprehensive health, dental, and vision coverage',
      icon: Heart
    },
    {
      title: 'Professional Growth',
      description: 'Continuous learning and conference attendance',
      icon: Zap
    }
  ]

  return (
    <>
      <Helmet>
        <title>Careers - DevSecRoot</title>
        <meta name="description" content="Join our team of security and development experts. Build your career at DevSecRoot and help secure the future of technology." />
        <link rel="canonical" href="https://devsecroot.com/careers" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-dsr-text mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Help us build a more secure digital future. Join a team of passionate experts 
              who are committed to excellence in security and development.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
                Our Culture
              </h2>
              <p className="text-lg text-dsr-text/70 mb-6">
                At DevSecRoot, we believe in fostering a culture of innovation, collaboration, 
                and continuous learning. Our team members are passionate about technology and 
                committed to making the digital world more secure.
              </p>
              <p className="text-lg text-dsr-text/70 mb-6">
                We value diversity, creativity, and the pursuit of excellence. Every team member 
                has the opportunity to grow, contribute, and make a real impact on our clients' success.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-6"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="glass-card-hover p-6 text-center"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="font-semibold text-dsr-text mb-2">{benefit.title}</h3>
                  <p className="text-sm text-dsr-text/70">{benefit.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Open Positions
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Ready to join our team? Explore our current openings and find the perfect role for you.
            </p>
          </motion.div>

          <div className="space-y-6">
            {openPositions.map((position, index) => (
              <motion.div
                key={position.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-dsr-text mb-2">{position.title}</h3>
                    <p className="text-dsr-text/70 mb-4">{position.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="px-3 py-1 bg-dsr-accent/20 text-dsr-accent rounded-full">
                        {position.department}
                      </span>
                      <span className="px-3 py-1 bg-dsr-card border border-white/10 text-dsr-text/70 rounded-full">
                        {position.location}
                      </span>
                      <span className="px-3 py-1 bg-dsr-card border border-white/10 text-dsr-text/70 rounded-full">
                        {position.type}
                      </span>
                    </div>
                  </div>
                  <div className="lg:flex-shrink-0">
                    <Link 
                      to={`/careers/${position.id}`}
                      className="btn-primary"
                    >
                      Apply Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Don't See the Right Role?
            </h2>
            <p className="text-xl text-dsr-text/70 mb-8 max-w-2xl mx-auto">
              We're always looking for talented individuals. Send us your resume and let's discuss 
              how you can contribute to our mission.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                <Mail className="w-5 h-5 mr-2" />
                Send Resume
              </Link>
              <Link to="/about" className="btn-secondary text-lg px-8 py-4">
                Learn More About Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default CareersPage
